import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Auth Store - Centralized authentication state with persistence
 * Replaces AuthContext for global access to auth state
 */
const useAuthStore = create(
  persist(
    (set, get) => ({
      // === STATE ===
      token: null,
      refreshToken: null,
      user: null,
      loading: false, // Loading state for async operations
      error: null,
      
      // === ACTIONS ===
      
      /**
       * Set authentication data after login
       */
      setAuth: (token, refreshToken, user) => {
        set({ 
          token, 
          refreshToken, 
          user, 
          error: null,
          loading: false 
        });
      },
      
      /**
       * Update only the user data (e.g., after plan change)
       */
      updateUser: (user) => {
        set({ user });
      },
      
      /**
       * Update tokens (e.g., after refresh)
       */
      updateTokens: (token, refreshToken) => {
        set(state => ({ 
          token, 
          refreshToken: refreshToken || state.refreshToken 
        }));
      },
      
      /**
       * Set loading state
       */
      setLoading: (loading) => set({ loading }),
      
      /**
       * Set error state
       */
      setError: (error) => set({ error }),
      
      /**
       * Clear error
       */
      clearError: () => set({ error: null }),
      
      /**
       * Logout - clear all auth state
       */
      logout: () => {
        // Clear any onboarding flags for current user
        const userId = get().user?.id;
        if (userId) {
          sessionStorage.removeItem(`onboarding_dismissed_${userId}`);
        }
        
        // Clear CSRF token from localStorage
        localStorage.removeItem('csrfToken');
        
        // Clear the persisted Zustand storage
        localStorage.removeItem('auth-storage');
        
        // Reset state
        set({ 
          token: null, 
          refreshToken: null, 
          user: null, 
          error: null,
          loading: false 
        });
      },
      
      /**
       * Check if user has specific permission
       */
      hasPermission: (permissionKey) => {
        const user = get().user;
        return user?.cuenta?.permissions?.[permissionKey] || false;
      }
    }),
    {
      name: 'auth-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist token, refreshToken, and user
      partialize: (state) => ({ 
        token: state.token, 
        refreshToken: state.refreshToken,
        user: state.user 
      }),
    }
  )
);

// Helper to check if hydration is complete
// Zustand persist v4 provides this via hasHydrated
export const waitForHydration = () => {
  return new Promise((resolve) => {
    // If already hydrated, resolve immediately
    if (useAuthStore.persist.hasHydrated()) {
      resolve();
      return;
    }
    // Otherwise wait for hydration
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      unsubscribe();
      resolve();
    });
  });
};

export default useAuthStore;
